import format from "date-fns/format";
import { enUS, ja } from "date-fns/locale";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { WeatherIcon } from "@/components/header/Weather/components/icons/WeatherIcon";
import { WeatherIconWithLabel } from "@/components/header/Weather/components/icons/WeatherIconWithLabel";
import { WindDegreeIconWithLabel } from "@/components/header/Weather/components/icons/WindDegreeIconWithLabel";
import { WeatherTableRow as TableRow } from "@/components/header/Weather/components/table/WeatherTabeRow";
import { WeatherTableData as TableData } from "@/components/header/Weather/components/table/WeatherTableData";
import { WeatherTableHeader as TableHeader } from "@/components/header/Weather/components/table/WeatherTableHeader";
import { WeatherTableRowHead as TableRowHead } from "@/components/header/Weather/components/table/WeatherTableRowHead";
import { useWeather } from "@/components/header/Weather/hooks/useWeather";
import { getWeatherCondition, WindDegree } from "@/models/weather";
import { useModal } from "@/sx-layout/components/plotmap/hooks";

export const Weather = () => {
  const { t } = useTranslation();
  const locale = localStorage.getItem("locale");
  const forecastsPopup = useModal();
  const weather = useWeather();
  const today = useMemo(() => {
    if (!weather.current?.forecast_datetime) return "";

    return format(new Date(weather.current.forecast_datetime), "M/d（E）", {
      locale: locale === "ja" ? ja : enUS,
    });
  }, [weather.current]);

  if (!weather.current) return <></>;

  return (
    <div className="relative">
      <div className="flex h-[45px]" onMouseEnter={forecastsPopup.open} onMouseLeave={forecastsPopup.close}>
        <div className="w-[45px] h-full flex items-center justify-center">
          <WeatherIcon weather={weather.current} />
        </div>
        <WindDegreeIconWithLabel
          weather={weather.current}
          color="white"
          label={
            <>
              {t(WindDegree[weather.current.wind_degree].i18n)}
              &nbsp;
              {`${Math.round(Number(weather.current.wind_speed))}m/s`}
            </>
          }
        />
      </div>
      {forecastsPopup.isOpen && (
        <div className="absolute min-w-[250px] bg-white text-[#111] px-[15px] pb-[15px] top-[50px] right-0 rounded-[2px] shadow-[0_2px_5px_rgba(0,0,0,0.3)]">
          <p className="leading-[30px]">{`${today} ${t("weather_every_3_hours")}`}</p>
          <table className="w-max border-t-[1px] border-l-[1px] border-t-[#adb5bd] border-l-[#adb5bd]">
            <thead>
              <TableRow>
                <TableHeader>{t("time")}</TableHeader>
                {weather.forecast.map((forecast, i) => {
                  const h = format(new Date(forecast.forecast_datetime), "H");
                  return <TableHeader key={i}>{h === "0" ? "24" : h}</TableHeader>;
                })}
              </TableRow>
            </thead>
            <tbody>
              {/* 天気 */}
              <TableRow>
                <TableRowHead>{t("weather")}</TableRowHead>
                {weather.forecast.map((forecast, i) => (
                  <TableData key={i}>
                    <WeatherIconWithLabel
                      weather={forecast}
                      label={t(getWeatherCondition(forecast.weather_condition).i18n)}
                    />
                  </TableData>
                ))}
              </TableRow>
              {/* 風向き */}
              <TableRow>
                <TableRowHead>{t("wind_degree")}</TableRowHead>
                {weather.forecast.map((forecast, i) => (
                  <TableData key={i}>
                    <WindDegreeIconWithLabel weather={forecast} label={t(WindDegree[forecast.wind_degree].i18n)} />
                  </TableData>
                ))}
              </TableRow>
              {/* 風速 */}
              <TableRow>
                <TableRowHead>{t("wind_speed")}</TableRowHead>
                {weather.forecast.map((forecast, i) => (
                  <TableData key={i} className="leading-[25px]">
                    {Math.round(Number(forecast.wind_speed))}
                  </TableData>
                ))}
              </TableRow>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
